import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { surveysService } from '@/services/surveys.service';
import { useAuthStore } from '@/stores/auth';
import { toast } from 'sonner';
import i18n from '@/i18n';
import { notifyApiError } from '@/lib/api-message';

export const surveyKeys = {
  all: ['surveys'] as const,
  list: (filters?: any) => [...surveyKeys.all, 'list', filters] as const,
  detail: (id: string) => [...surveyKeys.all, 'detail', id] as const,
  responses: (id: string) => [...surveyKeys.all, 'responses', id] as const,
  stats: (id: string) => [...surveyKeys.all, 'stats', id] as const,
  myPending: () => [...surveyKeys.all, 'my-pending'] as const,
  myResponses: () => [...surveyKeys.all, 'my-responses'] as const,
};

// Admin
export function useSurveys(params?: {
  page?: number;
  status?: string;
  productId?: string;
}) {
  return useQuery({
    queryKey: surveyKeys.list(params),
    queryFn: () => surveysService.findAll(params),
  });
}

export function useSurvey(id: string) {
  return useQuery({
    queryKey: surveyKeys.detail(id),
    queryFn: () => surveysService.findById(id),
    enabled: !!id,
  });
}

export function useCreateSurvey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: surveysService.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: surveyKeys.all });
      toast.success(i18n.t('survey:toast.created'));
    },
    onError: () => toast.error(i18n.t('survey:toast.createError')),
  });
}

export function useUpdateSurvey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...dto }: { id: string } & Record<string, any>) =>
      surveysService.update(id, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: surveyKeys.all });
      toast.success(i18n.t('survey:toast.updated'));
    },
    onError: () => toast.error(i18n.t('survey:toast.updateError')),
  });
}

export function useDeleteSurvey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: surveysService.delete,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: surveyKeys.all });
      toast.success(i18n.t('survey:toast.deleted'));
    },
    onError: () => toast.error(i18n.t('survey:toast.deleteError')),
  });
}

export function useDuplicateSurvey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: surveysService.duplicate,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: surveyKeys.all });
      toast.success(i18n.t('survey:toast.duplicated'));
    },
    onError: () => toast.error(i18n.t('survey:toast.duplicateError')),
  });
}

export function useSurveyResponses(surveyId: string, params?: any) {
  return useQuery({
    queryKey: surveyKeys.responses(surveyId),
    queryFn: () => surveysService.getResponses(surveyId, params),
    enabled: !!surveyId,
  });
}

export function useSurveyStats(surveyId: string) {
  return useQuery({
    queryKey: surveyKeys.stats(surveyId),
    queryFn: () => surveysService.getStats(surveyId),
    enabled: !!surveyId,
  });
}

// User
export function useMyPendingSurveys() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const accessToken = useAuthStore((s) => s.accessToken);
  const user = useAuthStore((s) => s.user);
  // Only run after user has accepted terms — avoids 401 during onboarding/terms flow
  const termsAccepted = !!(user as { termsAcceptedAt?: string | null })
    ?.termsAcceptedAt;
  return useQuery({
    queryKey: surveyKeys.myPending(),
    queryFn: surveysService.getMyPending,
    enabled: isAuthenticated && !!accessToken && termsAccepted,
  });
}

export function useMyResponses() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const accessToken = useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: surveyKeys.myResponses(),
    queryFn: surveysService.getMyResponses,
    enabled: isAuthenticated && !!accessToken,
  });
}

export function useRespondSurvey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      surveyId,
      answers,
      triggerType,
    }: {
      surveyId: string;
      answers: Array<{ questionId: string; value: string }>;
      triggerType?: string;
    }) => surveysService.respond(surveyId, answers, triggerType),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: surveyKeys.myPending() });
      qc.invalidateQueries({ queryKey: surveyKeys.myResponses() });
      toast.success(i18n.t('survey:toast.responseSent'));
    },
    onError: (err: any) =>
      notifyApiError(err, i18n.t('survey:toast.responseError')),
  });
}
